import { FOCUSED_PLACE_ZOOM } from "@/constants/map"
import { FlightMapRoute } from "@/types/flights/flight-map-route"
import { FlightSliceLayover } from "@/types/flights/flight-slice-layover"
import {
  Location,
  LocationSearchHit,
  Place,
  PlacePlaceholder,
} from "@/types/place"
import { BoundingBox, MapViewport } from "@/types/util"
import Supercluster, { PointFeature } from "supercluster"

import { getMapDivSize } from "@/lib/gmap-dom-utils"
import { isAirport } from "./entity-utils"

export async function geocode(
  request: google.maps.GeocoderRequest
): Promise<
  | [success: true, response: google.maps.GeocoderResponse]
  | [success: false, message: string]
> {
  const [{ Geocoder }, { MapsNetworkError }] = await Promise.all([
    google.maps.importLibrary("geocoding"),
    google.maps.importLibrary("core"),
  ])
  const geocoder = new Geocoder()
  try {
    const res = await geocoder.geocode(request)
    // even thought the type says it should always be defined, there are some HB's where this is null so going to check
    // for that and return an success=false in that case
    if (res?.results == null) {
      return [false, "Results are unexpectedly null"]
    }
    return [true, res]
  } catch (e) {
    if (e instanceof MapsNetworkError) {
      console.warn("Geocode was not successful: " + e.code)
      return [false, e.code]
    } else {
      throw e
    }
  }
}

const PDF_MAP_WIDTH = 640
const PDF_MAP_HEIGHT = 400
const MERCATOR_WORLD_PX = 256

function latRad(lat: number) {
  const sin = Math.sin((lat * Math.PI) / 180)
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
}

function pdfFitZoom(bounds: google.maps.LatLngBounds) {
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI
  const lngDiff = ne.lng() - sw.lng()
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360
  const latZoom = Math.floor(
    Math.log2(PDF_MAP_HEIGHT / MERCATOR_WORLD_PX / latFraction)
  )
  const lngZoom = Math.floor(
    Math.log2(PDF_MAP_WIDTH / MERCATOR_WORLD_PX / lngFraction)
  )
  return Math.max(Math.min(latZoom, lngZoom), 0)
}

export function readMapViewport(
  map: google.maps.Map | null
): MapViewport | null {
  const bounds = map?.getBounds()
  if (!bounds) return null

  const center = bounds.getCenter()
  return {
    lat: Number(center.lat().toFixed(6)),
    lng: Number(center.lng().toFixed(6)),
    zoom: pdfFitZoom(bounds),
  }
}

const DEFAULT_PADDING_PERCENTAGE = 0.12

export function setMapCenter({
  map,
  center,
  padding,
}: {
  map: google.maps.Map
  center: google.maps.LatLng
  padding?: google.maps.Padding
  onClick?: () => void
}) {
  map.setCenter(center)
  const panOffset = {
    x: -((padding?.left ?? 0) - (padding?.right ?? 0)) / 2,
    y: -((padding?.top ?? 0) - (padding?.bottom ?? 0)) / 2,
  }
  map.panBy(panOffset.x, panOffset.y)
}

/**
 * Fit map exactly to the given bounds. Use this for extending bounds
 * to include specific points (hover) or zooming into clusters.
 */
export function fitBoundsExact({
  map,
  bounds,
  percentage = DEFAULT_PADDING_PERCENTAGE,
  padding,
}: {
  map: google.maps.Map
  bounds: google.maps.LatLngBounds
  percentage?: number
  padding?: google.maps.Padding
}) {
  const mapDivSize = getMapDivSize(map)
  if (!mapDivSize) return

  const { width: mapWidth, height: mapHeight } = mapDivSize

  const visibleHeight =
    mapHeight - ((padding?.top ?? 0) + (padding?.bottom ?? 0))
  const visibleWidth = mapWidth - ((padding?.left ?? 0) + (padding?.right ?? 0))

  map.fitBounds(bounds, {
    top: visibleHeight * percentage + (padding?.top ?? 0),
    bottom: visibleHeight * percentage + (padding?.bottom ?? 0),
    left: visibleWidth * percentage + (padding?.left ?? 0),
    right: visibleWidth * percentage + (padding?.right ?? 0),
  })
  setMapCenter({ map, center: bounds.getCenter(), padding })
}

export interface ValidMarker {
  latitude: number
  longitude: number
}

export interface BoundsRect {
  north: number
  south: number
  east: number
  west: number
}

export interface LargestClusterResult {
  bounds: BoundsRect
  markers: ValidMarker[]
  markerCount: number
}

/**
 * Find the largest cluster of markers at a given zoom level.
 * Returns the bounds and markers of the largest cluster, or null if no markers.
 */
export function findLargestCluster(
  markers: ValidMarker[],
  zoom = 3
): LargestClusterResult | null {
  if (markers.length === 0) return null

  if (markers.length === 1) {
    return {
      bounds: {
        north: markers[0].latitude,
        south: markers[0].latitude,
        east: markers[0].longitude,
        west: markers[0].longitude,
      },
      markers: markers,
      markerCount: 1,
    }
  }

  // Create Supercluster instance
  const cluster = new Supercluster<{ index: number }, { count: number }>({
    radius: 60,
    maxZoom: 16,
    minPoints: 2,
    map: () => ({ count: 1 }),
    reduce: (acc, props) => {
      acc.count += props.count
    },
  })

  const points: PointFeature<{ index: number }>[] = markers.map(
    (marker, index) => ({
      type: "Feature" as const,
      properties: { index },
      geometry: {
        type: "Point" as const,
        coordinates: [marker.longitude, marker.latitude] as [number, number],
      },
    })
  )

  cluster.load(points)

  // Get clusters at the given zoom level
  const clusters = cluster.getClusters([-180, -85, 180, 85], zoom)

  if (clusters.length === 0) return null

  // Find the cluster with the most points
  let largestCluster = clusters[0]
  let maxPoints =
    "point_count" in largestCluster.properties
      ? largestCluster.properties.point_count
      : 1

  for (const c of clusters) {
    const pointCount =
      "point_count" in c.properties ? c.properties.point_count : 1
    if (pointCount > maxPoints) {
      maxPoints = pointCount
      largestCluster = c
    }
  }

  // Get the markers in the largest cluster
  let clusterMarkers: ValidMarker[]

  if (
    "cluster_id" in largestCluster.properties &&
    largestCluster.properties.cluster_id != null
  ) {
    // It's a cluster - get its leaves (individual points)
    const leaves = cluster.getLeaves(
      largestCluster.properties.cluster_id,
      Infinity
    )
    clusterMarkers = leaves.map((leaf) => ({
      latitude: leaf.geometry.coordinates[1],
      longitude: leaf.geometry.coordinates[0],
    }))
  } else {
    // It's a single point
    clusterMarkers = [
      {
        latitude: largestCluster.geometry.coordinates[1],
        longitude: largestCluster.geometry.coordinates[0],
      },
    ]
  }

  // Calculate bounds
  let north = -90,
    south = 90,
    east = -180,
    west = 180
  for (const m of clusterMarkers) {
    if (m.latitude > north) north = m.latitude
    if (m.latitude < south) south = m.latitude
    if (m.longitude > east) east = m.longitude
    if (m.longitude < west) west = m.longitude
  }

  return {
    bounds: { north, south, east, west },
    markers: clusterMarkers,
    markerCount: clusterMarkers.length,
  }
}

/**
 * Calculate bounds for an array of markers.
 */
export function calculateBounds(markers: ValidMarker[]): BoundsRect | null {
  if (markers.length === 0) return null

  let north = -90,
    south = 90,
    east = -180,
    west = 180

  for (const m of markers) {
    if (m.latitude > north) north = m.latitude
    if (m.latitude < south) south = m.latitude
    if (m.longitude > east) east = m.longitude
    if (m.longitude < west) west = m.longitude
  }

  return { north, south, east, west }
}

/**
 * Fit map to show markers, with automatic fallback to the densest cluster
 * when markers can't all fit in the viewport (e.g., spread globally with narrow map).
 */
export function fitMapToBounds<
  T extends { latitude?: number | null; longitude?: number | null },
>({
  map,
  markers,
  percentage = DEFAULT_PADDING_PERCENTAGE,
  padding,
  maxZoom = 16,
}: {
  map: google.maps.Map
  markers: T[]
  percentage?: number
  padding?: google.maps.Padding
  maxZoom?: number
}) {
  // Filter to valid markers with non-null coordinates
  const validMarkers: ValidMarker[] = []
  for (const m of markers) {
    if (m.latitude != null && m.longitude != null) {
      validMarkers.push({ latitude: m.latitude, longitude: m.longitude })
    }
  }

  if (validMarkers.length === 0) return

  // Single marker case - just center on it
  if (validMarkers.length === 1) {
    const center = new google.maps.LatLng(
      validMarkers[0].latitude,
      validMarkers[0].longitude
    )
    map.setCenter(center)
    map.setZoom(maxZoom)
    setMapCenter({ map, center, padding })
    return
  }

  // Calculate bounds for all markers
  const allBounds = new google.maps.LatLngBounds()
  validMarkers.forEach((marker) => {
    allBounds.extend(new google.maps.LatLng(marker.latitude, marker.longitude))
  })

  // Fit to all bounds first
  fitBoundsExact({ map, bounds: allBounds, percentage, padding })

  // Cap zoom level
  const zoom = map.getZoom()
  if (zoom && zoom > maxZoom) {
    map.setZoom(maxZoom)
  }

  // Check if all markers are visible in the viewport after fitting
  // We need to wait for the map to settle before checking bounds
  requestAnimationFrame(() => {
    const visibleBounds = map.getBounds()
    if (!visibleBounds) return

    const markersOutside = validMarkers.filter((marker) => {
      const latLng = new google.maps.LatLng(marker.latitude, marker.longitude)
      return !visibleBounds.contains(latLng)
    })

    // If all markers are visible, we're done
    if (markersOutside.length === 0) return

    // Only fall back to clustering if a significant portion of markers are outside
    // If just a few outliers are outside, keep the current view
    const outsidePercentage = markersOutside.length / validMarkers.length
    if (outsidePercentage < 0.3 && markersOutside.length <= 3) {
      // Few outliers - keep current bounds, don't over-optimize
      return
    }

    // Many markers are outside - find the best cluster to show
    // Use zoom level 1 to create large geographic clusters (continental scale)
    const largestClusterResult = findLargestCluster(validMarkers, 1)

    if (!largestClusterResult) return

    // Only use the cluster if it contains more markers than what's currently visible
    const markersInside = validMarkers.length - markersOutside.length
    if (largestClusterResult.markerCount <= markersInside) {
      // Current view already shows more markers, keep it
      return
    }

    // Fit to the largest cluster
    const clusterBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(
        largestClusterResult.bounds.south,
        largestClusterResult.bounds.west
      ),
      new google.maps.LatLng(
        largestClusterResult.bounds.north,
        largestClusterResult.bounds.east
      )
    )
    fitBoundsExact({ map, bounds: clusterBounds, percentage, padding })

    // Cap zoom level again
    const newZoom = map.getZoom()
    if (newZoom && newZoom > maxZoom) {
      map.setZoom(maxZoom)
    }
  })
}

export async function fitMapToLocation({
  map,
  location,
  padding,
  shouldApply,
}: {
  map: google.maps.Map
  location: Location | LocationSearchHit
  padding?: google.maps.Padding
  /**
   * Checked after the geocode round-trip. Return false to drop the fit when a
   * newer viewport request (e.g. a marker fit) has landed in the meantime.
   */
  shouldApply?: () => boolean
}) {
  let address = `${location.name}, ${location.category_label}`
  // exceptions google likes simplified
  const exceptions = new Set(["Bermuda"])

  if (exceptions.has(location.name)) {
    address = location.name
  }
  const [success, response] = await geocode({ address })

  if (shouldApply && !shouldApply()) return

  if (success && response.results.length > 0) {
    const bounds = response.results[0].geometry.viewport

    if (location.latitude != null && location.longitude != null) {
      const locationLatLng = new google.maps.LatLng(
        location.latitude,
        location.longitude
      )
      if (!bounds.contains(locationLatLng)) {
        bounds.extend(locationLatLng)
      }
    }

    map.fitBounds(bounds)
    setMapCenter({ map, center: bounds.getCenter(), padding })
    return
  }

  if (location.latitude != null && location.longitude != null) {
    const center = new google.maps.LatLng(location.latitude, location.longitude)
    setMapCenter({ map, center, padding })
    map.setZoom(FOCUSED_PLACE_ZOOM)
  }
}

/**
 * Compute a gently curved flight-path arc between two points.
 *
 * Instead of a true great circle (which swings dramatically toward the poles
 * on a Mercator map), this interpolates linearly in lat/lng and adds a small
 * perpendicular offset so the path bows away from the equator
 */
export function computeFlightArc(
  start: google.maps.LatLng,
  end: google.maps.LatLng,
  segments: number
): google.maps.LatLng[] {
  const lat1 = start.lat()
  const lng1 = start.lng()
  const lat2 = end.lat()

  let dLng = end.lng() - lng1
  if (dLng > 180) dLng -= 360
  if (dLng < -180) dLng += 360

  const dLat = lat2 - lat1
  const pathLen = Math.sqrt(dLat * dLat + dLng * dLng)

  if (pathLen < 1e-10) return [start, end]

  // Perpendicular unit vector in degree-space (rotated 90°)
  let perpLat = -dLng / pathLen
  let perpLng = dLat / pathLen

  // Always bow away from the equator
  const avgLat = (lat1 + lat2) / 2
  if ((avgLat >= 0 && perpLat < 0) || (avgLat < 0 && perpLat > 0)) {
    perpLat = -perpLat
    perpLng = -perpLng
  }

  const arcHeight = Math.min(pathLen * 0.08, 12)

  const path: google.maps.LatLng[] = []

  for (let i = 0; i <= segments; i++) {
    const f = i / segments
    const offset = arcHeight * Math.sin(Math.PI * f)

    const lat = lat1 + dLat * f + perpLat * offset
    let lng = lng1 + dLng * f + perpLng * offset

    if (lng > 180) lng -= 360
    else if (lng < -180) lng += 360

    path.push(new google.maps.LatLng(lat, lng))
  }

  return path
}

/**
 * Compute the complete flight path including any stops.
 * @param departure Departure LatLng point.
 * @param arrival Arrival LatLng point.
 * @param stops Array of LatLng points representing stops.
 * @param segments Number of segments to divide each path segment.
 * @returns Array of LatLng points representing the complete flight path.
 */
export function computeFlightPathWithStops(
  departure: google.maps.LatLng,
  arrival: google.maps.LatLng,
  stops: google.maps.LatLng[],
  segments: number
): google.maps.LatLng[] {
  const waypoints = [departure, ...stops, arrival]
  const flightPath: google.maps.LatLng[] = []

  // Compute the path for each segment of the journey.
  for (let i = 0; i < waypoints.length - 1; i++) {
    const segmentPath = computeFlightArc(
      waypoints[i],
      waypoints[i + 1],
      segments
    )
    flightPath.push(...segmentPath)
  }

  return flightPath
}

/**
 * Calculate the distance between two LatLng points using the Haversine formula.
 * @param point1 First LatLng point.
 * @param point2 Second LatLng point.
 * @returns Distance in meters between the two points.
 */
function calculateDistance(
  point1: google.maps.LatLng,
  point2: google.maps.LatLng
): number {
  const R = 6371e3 // Radius of the Earth in meters.
  // Convert latitudes to radians.
  const φ1 = (point1.lat() * Math.PI) / 180
  const φ2 = (point2.lat() * Math.PI) / 180
  const Δφ = ((point2.lat() - point1.lat()) * Math.PI) / 180
  const Δλ = ((point2.lng() - point1.lng()) * Math.PI) / 180

  // Compute the distance using the Haversine formula.
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c // Distance in meters.

  return distance
}

/**
 * Determine the number of segments based on total distance.
 * @param distance Total distance in meters.
 * @returns Number of segments.
 */
export function determineSegments(distance: number): number {
  return Math.max(24, Math.ceil((distance / 500000) * 10))
}

/**
 * Generate the complete flight path including stops and departure/arrival.
 * @param flightData Data containing details about the flight.
 * @returns Array of LatLng points representing the full flight path.
 */
export function generateFullFlightPath(
  flightData: FlightMapRoute
): google.maps.LatLng[] {
  const departureLocation =
    flightData.departure_airport.latitude != null &&
    flightData.departure_airport.longitude != null
      ? new google.maps.LatLng(
          flightData.departure_airport.latitude,
          flightData.departure_airport.longitude
        )
      : null

  const arrivalLocation =
    flightData.arrival_airport.latitude != null &&
    flightData.arrival_airport.longitude != null
      ? new google.maps.LatLng(
          flightData.arrival_airport.latitude,
          flightData.arrival_airport.longitude
        )
      : null

  // If either departure or arrival location is invalid, return an empty array
  if (!departureLocation || !arrivalLocation) {
    return []
  }

  const stops: google.maps.LatLng[] = []

  flightData.layovers.forEach((layover: FlightSliceLayover) => {
    if (layover.airport.latitude != null && layover.airport.longitude != null) {
      stops.push(
        new google.maps.LatLng(
          layover.airport.latitude,
          layover.airport.longitude
        )
      )
    }
  })

  // Calculate the total distance of the flight including stops.
  let totalDistance = 0
  let previousLocation = departureLocation
  for (const stop of stops) {
    totalDistance += calculateDistance(previousLocation, stop)
    previousLocation = stop
  }
  totalDistance += calculateDistance(previousLocation, arrivalLocation)

  // Determine number of segments based on the total distance.
  const segments = determineSegments(totalDistance)

  // Return the complete flight path including departure, stops, and arrival.
  return computeFlightPathWithStops(
    departureLocation,
    arrivalLocation,
    stops,
    segments
  )
}

// remove stuff in parens from the end of name, often a translation
//                                  |------- remove this -------|
// e.g. Peggy Guggenheim Collection (Collezione Peggy Guggenheim)
export function formatPlaceName(name: string) {
  return name.replace(/\s*\(.+\)$/, "")
}

export function getPlaceName(place: Place | PlacePlaceholder) {
  return isAirport(place) ? place.code : formatPlaceName(place.name)
}

/**
 * Converts Google Maps bounds into a backend bounding box, or `undefined`
 * when the bounds are degenerate (the backend rejects boxes where
 * north_lat <= south_lat or west_long == east_long).
 */
export function createBboxFromMapBounds(
  bounds: google.maps.LatLngBounds
): BoundingBox | undefined {
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()

  // Handle latitude normally (no wrapping issues)
  const minLat = Math.min(ne.lat(), sw.lat())
  const maxLat = Math.max(ne.lat(), sw.lat())

  // A zero-height viewport (e.g. a hidden or mid-layout map container)
  // yields a degenerate box that can't be searched.
  if (minLat === maxLat) return undefined

  const neLng = ne.lng()
  let swLng = sw.lng()

  // Google Maps normalizes longitudes to [-180, 180], so a viewport crossing
  // the International Date Line comes back with sw.lng() > ne.lng(). Shift
  // the western edge below -180 to preserve the crossing with west < east.
  if (swLng > neLng) {
    swLng -= 360
  }

  // A viewport spanning the whole world can collapse both edges to the same
  // longitude (e.g. -172 == -172). Send the full world instead.
  if (swLng === neLng) {
    return [minLat, maxLat, -180, 180]
  }

  return [minLat, maxLat, swLng, neLng]
}
